import { Controller } from "@hotwired/stimulus";

// Usage: add data-controller="number-format" to an input
// Optional values:
// - data-number-format-decimals-value: number of decimals (default 0)
// - data-number-format-hidden-name-value: name for hidden raw input (if provided, visible input name is removed)
// - data-number-format-unit-value: string unit to append after formatted value (e.g. "€", "%")
// Behavior:
// - Formats value for display in fr-FR on blur
// - Shows raw unformatted on focus
// - Keeps raw numeric in dataset.raw and (optionally) in a hidden input for submission
export default class extends Controller {
    static values = {
        decimals: { type: Number, default: 0 },
        hiddenName: String,
        unit: String,
    };

    connect() {
        this.input = this.element;
        this.input.setAttribute("inputmode", "decimal");
        // Switch to text to avoid native number quirks and allow formatting
        try { this.input.type = "text"; } catch (_) {}

        // Create hidden raw field if requested
        this.hidden = document.createElement("input");
        this.hidden.type = "hidden";
        this.hidden.name = this.input.name;
        this.input.parentNode.insertBefore(this.hidden, this.input.nextSibling);

        // Initial normalization
        const raw = this.parseToNumber(this.input.value);
        this.setRaw(raw);
        this.formatDisplay();

        // Events
        this.onBlur = () => { this.normalizeAndFormat(); };
        this.onFocus = () => { this.showRaw(); };
        this.onInput = () => { this.normalizeOnly(); };
        this.input.addEventListener("focus", this.onFocus);
        this.input.addEventListener("blur", this.onBlur);
        this.input.addEventListener("input", this.onInput);
    }

    disconnect() {
        if (!this.input) return;
        this.input.removeEventListener("focus", this.onFocus);
        this.input.removeEventListener("blur", this.onBlur);
        this.input.removeEventListener("input", this.onInput);
    }

    normalizeOnly() {
        // Do not reformat display while typing; just update raw
        const raw = this.parseToNumber(this.input.value);
        this.setRaw(raw);
    }

    normalizeAndFormat() {
        const raw = this.parseToNumber(this.input.value);
        this.setRaw(raw);
        this.formatDisplay();
    }

    showRaw() {
        const raw = this.getRaw();
        if (raw === null || isNaN(raw)) return;
        const str = this.decimalsValue > 0 ? raw.toFixed(this.decimalsValue) : String(Math.trunc(raw));
        this.input.value = str.replace(".", ",");
    }

    formatDisplay() {
        const raw = this.getRaw();
        if (raw === null || isNaN(raw)) {
            this.input.value = "";
            return;
        }
        const nf = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: this.decimalsValue });
        const base = nf.format(raw);
        this.input.value = this.hasUnitValue && this.unitValue ? `${base} ${this.unitValue}` : base;
    }

    setRaw(n) {
        if (n === null || isNaN(n)) {
            this.input.dataset.raw = "";
            if (this.hidden) this.hidden.value = "";
            return;
        }
        const fixed = this.decimalsValue > 0 ? Number(n.toFixed(this.decimalsValue)) : Math.trunc(n);
        this.input.dataset.raw = String(fixed);
        if (this.hidden) this.hidden.value = String(fixed);
    }

    getRaw() {
        const v = this.input.dataset.raw;
        if (v === undefined || v === "") return null;
        return Number(v);
    }

    parseToNumber(str) {
        if (str == null) return null;
        const s = String(str).replace(/\s|\u00A0/g, "").replace(/,/g, ".").replace(/[^0-9+\-.]/g, "");
        if (s === "" || s === "-" || s === "." || s === "-") return null;
        const n = Number(s);
        return isNaN(n) ? null : n;
    }
}